import React from "react";
import SVG from "react-inlinesvg";
import { CartReferenceNumber } from "tsi-common-react/src/apps/checkout/CartReferenceNumber";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import sealyLogoMobile from "../assets/svg/sealy-logo-wordmark.svg";
import sealyLogo from "../assets/svg/sealy-logo.svg";
import sealyStearnsLogo from "../assets/svg/sealy-stearns-logo.svg";
import stearnsLogo from "../assets/svg/stearns-logo.svg";
import tempurpedicLogo from "../assets/svg/tempur-logo-text.svg";

type BrandingConfig = {
    title: string;
    logo: string;
    logoMobile: string;
    logoClassname: string;
};

const CheckoutLogoLink = (props: {
    href: string;
    branding: BrandingConfig;
    className?: string;
}): React.ReactNode => {
    const { href, branding, className } = props;

    return (
        <a
            className={
                className
                    ? `site-nav__logo-link ${className}`
                    : "site-nav__logo-link"
            }
            href={href}
            aria-label={branding.title}
        >
            {/*
              Name lives on the <a> so the home link stays labeled during
              SSR / while react-inlinesvg loads (or if the fetch fails).
              The SVG is decorative once the link has its own name (A360 #138).
            */}
            <SVG
                className={branding.logoClassname}
                src={branding.logo}
                aria-hidden="true"
                focusable="false"
            />
        </a>
    );
};

const getBranding = (appSlug: string): BrandingConfig => {
    switch (appSlug) {
        case "stearnswarranty": {
            return {
                title: t`Stearns & Foster`,
                logo: stearnsLogo,
                logoMobile: "",
                logoClassname:
                    "site-nav__logo site-nav__logo--checkout-stearns",
            };
        }
        case "sealywarranty": {
            return {
                title: t`Sealy`,
                logo: sealyLogo,
                logoMobile: sealyLogoMobile,
                logoClassname: "site-nav__logo site-nav__logo--checkout-sealy",
            };
        }
        case "sealyemployees":
        case "sealyretail": {
            return {
                title: t`Sealy | Stearns & Foster Home`,
                logo: sealyStearnsLogo,
                logoMobile: "",
                logoClassname: "site-nav__logo site-nav__logo--checkout-alt",
            };
        }
        default: {
            return {
                title: t`Tempur-Pedic Home`,
                logo: tempurpedicLogo,
                logoMobile: "",
                logoClassname: "site-nav__logo site-nav__logo--checkout",
            };
        }
    }
};

export const Template = (
    props: templates.CheckoutHeaderTemplate,
): React.ReactNode => {
    const secure = (
        <span
            key="secure"
            className="checkout-header__header--bold"
        >{t`Secure`}</span>
    );
    const secureCheckout = jt`${secure} Checkout`;
    const branding = getBranding(props.app_slug);

    return (
        <header className="l-full-width">
            <nav className="site-nav checkout-header">
                <div className="site-nav__content checkout-header__content l-capped-width">
                    <CheckoutLogoLink
                        href={props.root_page_url}
                        branding={branding}
                    />

                    {branding.logoMobile && (
                        <CheckoutLogoLink
                            href={props.root_page_url}
                            branding={{
                                ...branding,
                                logo: branding.logoMobile,
                            }}
                            className="site-nav__logo-link--mobile"
                        />
                    )}

                    <h1 className="checkout-header__header">
                        {secureCheckout}
                    </h1>

                    <div className="checkout-header__header-cart-ref">
                        <CartReferenceNumber
                            encodedBasketID={props.encoded_basket_id}
                            phoneNumber={props.support_website_phone_number}
                        />
                    </div>
                </div>
            </nav>
        </header>
    );
};
